import { supabase } from '../utils/supabase';
import { getOAuthCallbackUrl } from '../utils/redirectUrls';
import rateLimiter, { checkRateLimit } from '../utils/rateLimiter';
import logAuthEvent from '../utils/logAuthEvent';

/**
 * Initiate Google OAuth sign-in via Supabase.
 *
 * SECURITY:
 *   - redirectTo is always the fixed /auth/callback URL from getOAuthCallbackUrl().
 *     Never pass a user-controlled redirect destination.
 *   - The Google client secret never appears here — it lives only in the
 *     Supabase Dashboard / CLI environment variables.
 *   - PKCE is handled automatically by the Supabase JS client (flowType: 'pkce').
 *
 * RATE LIMITING:
 *   - Uses the existing 'auth' limiter (5 requests / minute) to prevent rapid
 *     repeated OAuth initiations.
 *
 * @returns {Promise<void>} resolves when redirect is initiated; throws on error.
 */
export const signInWithGoogle = async () => {
  // Enforce client-side rate limit — same bucket as password login.
  if (!rateLimiter.isAllowed('auth')) {
    const ms = rateLimiter.getTimeUntilReset('auth');
    const seconds = Math.ceil(ms / 1000);
    throw new Error(`Too many sign-in attempts. Please wait ${seconds} second${seconds !== 1 ? 's' : ''} and try again.`);
  }

  logAuthEvent('oauth_start');

  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: getOAuthCallbackUrl(),
      // prompt: 'select_account' — uncomment if this app is used on shared devices
      // so users can choose between multiple Google accounts.
      queryParams: {
        access_type: 'online',
        prompt: 'select_account',
      },
    },
  });

  if (error) {
    logAuthEvent('oauth_start_error');
    throw error;
  }
  // Browser redirects to Google; this function does not return normally.
};
